/**
 * The studio's own product line.
 *
 * This section is evidence, not a portfolio. The argument it makes is in the
 * lead: a studio that funds and maintains its own software in the stores runs a
 * release process it cannot walk away from.
 *
 * Which means the statuses have to stay true. They are dictionary copy so they
 * can be corrected in one place, and each card links out to the live site so a
 * prospective client can check the claim rather than take it.
 *
 * The screenshot at the top of each card is captured from the live site in the
 * visitor's own language, so a Spanish reader sees the Spanish page they would
 * actually land on.
 */
import { Section } from '../components/Section';
import { useI18n } from '../i18n';
import { PRODUCTS } from '../lib/constants';
import styles from './Products.module.css';

/**
 * The screenshots, fingerprinted by the build.
 *
 * They live in src/client/assets rather than public/ so Vite content-hashes
 * them — `rickey-en-a1b2c3.png`. Anything in public/ is copied verbatim and
 * keeps its name forever, which means a new screenshot under an old filename
 * is invisible to every visitor holding a cached copy. That is not theoretical:
 * it is exactly what happened when these were re-shot in light mode, and the
 * one-hour cache on public/ hid the fix.
 *
 * Hashed names also earn the immutable one-year cache instead of an hour.
 *
 * `eager` because there are six small files and a lazy import would make the
 * card wait on a second round trip to discover its own image.
 */
const SHOTS = import.meta.glob('../assets/products/*.png', {
  eager: true,
  import: 'default',
}) as Record<string, string>;

const shotFor = (slug: string, locale: string): string | undefined =>
  SHOTS[`../assets/products/${slug}-${locale}.png`];

type Tone = (typeof PRODUCTS)[number]['tone'];

/** Green means shipped and nothing else; the earlier states are quieter. See tokens.css. */
const pillClass = (tone: Tone | undefined): string =>
  tone && tone !== 'live' ? `pill pill--${tone}` : 'pill';

export function Products() {
  const { t, locale } = useI18n();

  return (
    <Section
      id="products"
      eyebrow={t.products.eyebrow}
      title={t.products.title}
      lead={t.products.lead}
    >
      <div className={`grid ${styles.grid}`}>
        {t.products.items.map((item) => {
          const product = PRODUCTS.find((entry) => entry.name === item.name);
          return (
            <article key={item.name} className={`cell ${styles.card}`}>
              {product && shotFor(product.slug, locale) ? (
                <div className={styles.shot}>
                  <img
                    className={styles.image}
                    src={shotFor(product.slug, locale)}
                    alt={item.imageAlt}
                    /*
                     * Stated so the browser reserves the space before the file
                     * arrives. Without them the three cards jump as each one
                     * loads, which is the layout shift Core Web Vitals measures.
                     */
                    width={800}
                    height={450}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              ) : null}
              <div className={styles.content}>
                <div className={styles.head}>
                  <h3 className={styles.name}>{item.name}</h3>
                  <span className={pillClass(product?.tone)}>{item.status}</span>
                </div>
                <p className={styles.body}>{item.body}</p>
                <p className={`meta ${styles.stack}`}>{product?.stack}</p>
                <p className="meta">{item.progress}</p>
                {product ? (
                  <a
                    className={styles.link}
                    href={product.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {product.domain} <span aria-hidden="true">→</span>
                    <span className="sr-only"> — {t.products.visit}</span>
                  </a>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
    </Section>
  );
}
